import type { Source } from "@/types";

/**
 * JWT-claims → app-scope resolution (port of dev's `src/lib/authScope.ts`).
 *
 * Kept dependency-free (types only) so both `@/lib/session` and the auth
 * views can import it without cycles.
 */

export interface JwtClaims {
  sub?: string;
  role?: string;
  staffType?: string | null;
  staffTypeId?: number | null;
  source?: string;
  targetDatabase?: string;
  merchantId?: string;
  isAgent?: boolean;
  clinicId?: number;
  clientId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
  /**
   * The caller's FULL hierarchy chain, minted alongside the single-level
   * `staffType`/`staffTypeId` pair. Reading these means the ancestor chain is
   * known on the FIRST render instead of only after `/auth/me` returns —
   * which is what scope-defaulted dropdowns need, since a child list cannot
   * be fetched without its parent ids. Absent on older tokens; the `/auth/me`
   * merge still fills the gaps.
   */
  scopeClientId?: number | string | null;
  scopeEmployerId?: number | string | null;
  scopeGroupId?: number | string | null;
  scopeDivisionId?: number | string | null;
  scopeLocationId?: number | string | null;
  [key: string]: unknown;
}

export interface AccessContext {
  role?: string;
  staffType?: string;
  source?: Source;
  scope?: Source;
  isAgent: boolean;
  merchantId?: string;
  staffTypeId?: number;
  clientId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
  clinicId?: number;
}

/** Agent mapping row from the login context / `GET /auth/me` (rendering only). */
export interface AgentMappingContext {
  agentMappingId?: number;
  agentCode?: string;
  hierarchyLevel?: string;
  /** Visibility role: AGENT, AGENCY_ADMIN, MASTER_AGENCY_ADMIN, … */
  role?: string;
  clientId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
}

/** The `GET /auth/me` rendering context (not an authority source). */
export interface UserLoginContext {
  userId?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  merchantId?: string;
  role?: string;
  features?: string[];
  /** Subset of `features` the caller may WRITE; the rest are view-only. */
  writableFeatures?: string[];
  /**
   * Every navigation id this caller may open, resolved by the backend from
   * role, seat and agent flag. The sidebar renders exactly these; it holds no
   * visibility rule of its own.
   */
  visibleMenuIds?: string[];
  /** Insurance levels the caller may administer, strictly below their seat. */
  manageableLevels?: string[];
  /**
   * Levels the caller may STAFF — the User dialog's type list, and the only
   * field that should drive it. Unlike `manageableLevels` this includes the
   * caller's own level: an employer admin staffs their own employer. Empty for
   * an agent, clinic or member seat, which staff nobody.
   */
  assignableUserLevels?: string[];
  /** Commission sections the caller may open (DASHBOARD, RULES, AGENCIES, …). */
  commissionSections?: string[];
  /**
   * The account was created (or had its password set by an admin) with a
   * password somebody else chose, so it must be changed before anything else.
   * Not advisory: while it is set the server refuses all user administration
   * from this account. Cleared by choosing your own password.
   */
  requirePasswordChange?: boolean;
  userType?: string;
  isAgent?: boolean;
  scope?: {
    clientId?: number;
    employerId?: number;
    groupId?: number;
    divisionId?: number;
    locationId?: number;
    memberId?: number;
    clinicId?: number;
  };
  agentMappings?: AgentMappingContext[];
}

/**
 * Backend `staffType` → frontend scope. Every seat the backend can mint must
 * map here — a CLIENT login is the "payer" scope and PROVIDER/ADMIN staff are
 * clinic seats. An unmapped staffType resolves to `undefined` (restrictive),
 * never falls through to "show everything".
 *
 * Exception: platform PGLX tokens with ADMIN staffType/role are super-admins
 * (full nav). Hardened backend JWTs often carry both `source: PGLX` and
 * `staffType: ADMIN` for those users.
 */
const STAFF_TYPE_TO_SCOPE: Record<string, Source> = {
  PROVIDER: "clinic",
  ADMIN: "clinic",
  CLIENT: "payer",
  EMPLOYER: "employer",
  GROUP: "group",
  DIVISION: "division",
  LOCATION: "location",
  MEMBER: "member",
};

const isPlatformSource = (value?: string): boolean => value?.toUpperCase() === "PGLX";

/** Seats inside a client's hierarchy — never a platform role, whatever `role` says. */
const HIERARCHY_STAFF_TYPES = new Set([
  "CLIENT",
  "EMPLOYER",
  "GROUP",
  "DIVISION",
  "LOCATION",
  "MEMBER",
]);

const isAdminClaim = (value?: string): boolean => {
  const v = value?.toUpperCase();
  return v === "ADMIN" || v === "ROLE_ADMIN";
};

/**
 * Resolve the app scope from JWT claims.
 *
 * Precedence:
 * 1. SUPER_ADMIN → `pglx`, whatever seat the row happens to carry
 * 2. Platform PGLX + ADMIN (role or staffType) → `pglx` (full sidebar)
 * 3. staffType seat mapping (CLIENT → payer, PROVIDER/ADMIN → clinic, …)
 * 4. Platform PGLX with no staff seat → `pglx`
 */
export function resolveScope(claims: {
  staffType?: string;
  source?: string;
  targetDatabase?: string;
  role?: string;
}): Source | undefined {
  if (claims.role?.toUpperCase() === "SUPER_ADMIN") return "pglx";

  const staffType = claims.staffType?.toUpperCase();
  const isPlatform = isPlatformSource(claims.source) || isPlatformSource(claims.targetDatabase);

  // A hierarchy seat is a seat whatever the role says. Tonya — BEREI, role ADMIN, staffType
  // EMPLOYER, target database PGLX — is Marpai's employer admin, not the platform's: read as
  // "pglx" she was offered every client in the book. The platform shortcut below is only for
  // staff with no hierarchy seat at all.
  if (staffType && HIERARCHY_STAFF_TYPES.has(staffType)) {
    return STAFF_TYPE_TO_SCOPE[staffType];
  }

  // Hardened JWT shape: source=PGLX + staffType/role=ADMIN → platform super-admin
  if (isPlatform && (isAdminClaim(staffType) || isAdminClaim(claims.role))) {
    return "pglx";
  }

  if (staffType) return STAFF_TYPE_TO_SCOPE[staffType];
  if (isPlatform) return "pglx";
  return undefined;
}

/**
 * Map the token's `staffTypeId` onto the matching entity id (dev parity):
 * a CLIENT seat's staffTypeId is its clientId, a PROVIDER/ADMIN seat's is
 * its clinicId, and so on. Explicit per-entity claims win over the fallback.
 */
export function resolveStaffIds(claims: {
  staffType?: string;
  staffTypeId?: number;
  clientId?: number;
  clinicId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
}): {
  clientId?: number;
  clinicId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
} {
  const kind = claims.staffType?.toLowerCase();
  const id = claims.staffTypeId;
  const pick = (k: string, fallback?: number) => (kind === k && id != null ? id : fallback);
  return {
    clientId: pick("client", claims.clientId),
    clinicId: kind === "provider" || kind === "admin" ? (id ?? claims.clinicId) : claims.clinicId,
    employerId: pick("employer", claims.employerId),
    groupId: pick("group", claims.groupId),
    divisionId: pick("division", claims.divisionId),
    locationId: pick("location", claims.locationId),
    memberId: pick("member", claims.memberId),
  };
}

/** A hierarchy id claim that is usable: present, numeric, non-zero. */
function asId(value: unknown): number | undefined {
  if (value == null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Single entry point for reading a decoded JWT payload into app access
 * context. Reads the `scope*` chain claims as the base so the ancestor chain
 * is known on the first render, then lets the explicit per-level claims and
 * the caller's own `staffTypeId` seat win over them.
 */
export function resolveAccessContext(payload: JwtClaims | null | undefined): AccessContext {
  const p = payload ?? {};
  const role = typeof p.role === "string" ? p.role : undefined;
  const staffType = typeof p.staffType === "string" ? p.staffType : undefined;
  const scope = resolveScope({
    role,
    staffType,
    source: p.source,
    targetDatabase: p.targetDatabase,
  });

  const seat = resolveStaffIds({
    staffType,
    staffTypeId: asId(p.staffTypeId),
    clientId: asId(p.clientId) ?? asId(p.scopeClientId),
    clinicId: asId(p.clinicId),
    employerId: asId(p.employerId) ?? asId(p.scopeEmployerId),
    groupId: asId(p.groupId) ?? asId(p.scopeGroupId),
    divisionId: asId(p.divisionId) ?? asId(p.scopeDivisionId),
    locationId: asId(p.locationId) ?? asId(p.scopeLocationId),
    memberId: asId(p.memberId),
  });

  return {
    role: role?.toLowerCase(),
    staffType,
    source: scope,
    scope,
    isAgent: p.isAgent === true,
    merchantId: p.merchantId ?? p.targetDatabase,
    staffTypeId: asId(p.staffTypeId),
    ...seat,
  };
}

/** Only ADMIN and SUPER_ADMIN may create or update users. */
export function canManageUsers(role?: string): boolean {
  const upper = role?.toUpperCase();
  return upper === "ADMIN" || upper === "SUPER_ADMIN";
}

/**
 * Roles allowed to WRITE commission data — mirrors the backend's writer set.
 * The backend is the enforcement; this only stops a viewer being shown
 * buttons that would 403.
 */
const COMMISSION_WRITER_ROLES = new Set([
  "ADMIN",
  "SUPER_ADMIN",
  "FINANCE_ADMIN",
  "MEMBER_ADMIN",
  "API_CLIENT",
  "AGENCY_ADMIN",
]);

export function canWriteCommission(role?: string): boolean {
  return !!role && COMMISSION_WRITER_ROLES.has(role.toUpperCase());
}

/**
 * Prefer this over the role mirror above wherever `writableFeatures` from
 * `/auth/me` is available — it cannot drift from the server's write guards.
 */
export function canWriteFeature(
  writableFeatures: string[] | null | undefined,
  feature: string
): boolean {
  const target = feature.toUpperCase();
  return (writableFeatures ?? []).some((f) => f?.toUpperCase() === target);
}

/**
 * Role strength, mirroring the backend's ranking. Create/update reject a
 * request whose role outranks the caller's, so an ADMIN can no longer mint a
 * SUPER_ADMIN that would then bypass every level, subtree and merchant check.
 */
const ROLE_RANK: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  FINANCE_ADMIN: 60,
  API_CLIENT: 60,
  MEMBER_ADMIN: 60,
  CUSTOMER_ADMIN: 40,
  AGENCY_ADMIN: 30,
  CUSTOMER_SUPPORT: 20,
  READ_ONLY: 10,
};

/** 0 for an unknown role, so it can never outrank a real one. */
export function roleRank(role?: string | null): number {
  if (!role) return 0;
  return ROLE_RANK[role.trim().toUpperCase()] ?? 0;
}

/**
 * May `callerRole` assign — or edit a user holding — `targetRole`? Equal rank
 * is allowed: an ADMIN may create another ADMIN. Mirrors the server guard so
 * the UI can hide the option instead of ending in a 403.
 */
export function canAssignRole(callerRole?: string | null, targetRole?: string | null): boolean {
  if (!callerRole) return false;
  return roleRank(targetRole) <= roleRank(callerRole);
}

export function isPlatform(scope?: Source): boolean {
  return scope === "pglx";
}

/**
 * Commission-only shell (agent / agency-admin portal). This shell is for
 * AGENTS only — the backend flags them with `isAgent`. A non-agent whose
 * features happen to be exactly ["COMMISSION"] (a read-only commission
 * viewer) gets the normal console with just the commission area visible.
 */
export function isCommissionOnlyMenu(_features?: string[] | null, isAgent?: boolean): boolean {
  return isAgent === true;
}

/** True when any active mapping is an agency-admin (or master) role. */
export function isAgencyAdmin(agentMappings?: AgentMappingContext[] | null): boolean {
  return (agentMappings ?? []).some((m) => {
    const r = m.role?.toUpperCase();
    return r === "AGENCY_ADMIN" || r === "MASTER_AGENCY_ADMIN";
  });
}

/** Primary agent code from the first mapping (the agency brokerId for admins). */
export function primaryAgentCode(agentMappings?: AgentMappingContext[] | null): string | null {
  return agentMappings?.[0]?.agentCode?.trim() || null;
}
